import { useState, useEffect } from 'react';
import { FieldTypeMetadata, FieldValue } from '../../types';
import { DataInputText } from './DataInputText';
import { DataInputSelect } from './DataInputSelect';
import { DataInputDate } from './DataInputDate';
import { DataInputMoney } from './DataInputMoney';
import { DataInputPercentage } from './DataInputPercentage';
import { DataInputTextarea } from './DataInputTextarea';

type DataInputFieldProps = {
  metadata: FieldTypeMetadata;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  onCancel: () => void;
  highlight?: 'new' | 'existing';
  fieldName?: string;
  ariaLabelledBy?: string;
  isLongValue?: boolean;
};

export type CommonInputProps = {
  inputValue: string | Date | null;
  setInputValue: (value: string | Date | null) => void;
  onSave: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  variant: 'inline';
  autoFocus: boolean;
  ariaLabelledBy?: string;
};

export default function DataInputField({
  metadata,
  value,
  onChange,
  onCancel,
  ariaLabelledBy,
  fieldName,
  isLongValue = false,
}: DataInputFieldProps) {
  const getInitialValue = () => {
    if (metadata.type === 'DATE') {
      return value ? new Date(String(value)) : null;
    }
    return String(value || '');
  };

  const [inputValue, setInputValue] = useState(getInitialValue);

  useEffect(() => {
    setInputValue(getInitialValue());
  }, [value]);

  const parseValue = (val: string | Date | null): FieldValue => {
    switch (metadata.type) {
      case 'DATE':
        if (!val) return '';
        if (val instanceof Date) return val.toISOString();
        return new Date(val).toISOString();
      case 'MONEY':
      case 'PERCENTAGE':
        return Number(val) || 0;
      case 'BOOLEAN':
        return val === 'true';
      default:
        return val as string;
    }
  };

  const handleSave = () => {
    const parsedValue = parseValue(inputValue);
    onChange(parsedValue);
  };

  const handleCancel = () => {
    setInputValue(getInitialValue());
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isTextarea = metadata.type === 'TEXT';
    if ((isTextarea && e.key === 'Enter' && e.ctrlKey) || (!isTextarea && e.key === 'Enter')) {
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const commonProps: CommonInputProps = {
    inputValue,
    setInputValue,
    onSave: handleSave,
    onKeyDown: handleKeyDown,
    variant: 'inline',
    autoFocus: true,
    ariaLabelledBy,
  };

  if (isLongValue && canEditAsLongText(metadata, fieldName)) {
    return <DataInputTextarea {...commonProps} minRows={1} maxRows={10} width="100%" />;
  }

  switch (metadata.type) {
    case 'SELECT':
    case 'BOOLEAN':
      return (
        <DataInputSelect
          {...commonProps}
          options={metadata.options
            .map(option => ({
              value: String(option.value),
              label: option.label,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))}
        />
      );
    case 'DATE':
      return <DataInputDate {...commonProps} valueFormat={metadata.valueFormat} />;
    case 'MONEY': {
      const currencySymbol = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: metadata.currency,
        maximumSignificantDigits: 1,
      }).formatToParts(0)[0].value;
      return <DataInputMoney {...commonProps} currencySymbol={currencySymbol} />;
    }
    case 'PERCENTAGE':
      return <DataInputPercentage {...commonProps} />;
    case 'TEXT':
      return <DataInputTextarea {...commonProps} />;
    case 'STRING':
    case 'NUMBER':
    default:
      if (fieldName === 'description') {
        return <DataInputTextarea {...commonProps} />;
      }
      return <DataInputText {...commonProps} />;
  }
}

export function canEditAsLongText(metadata: FieldTypeMetadata, fieldName?: string) {
  return metadata.type === 'STRING' && fieldName !== 'description';
}
